import { useState } from 'react';
import { HelpCentreListInput } from './HelpCentreList.types';
import Card from '@tempcover/bolt-ui/card';
import { Accordion } from '../Accordion/Accordion';
import { LinkButton } from '../LinkButton/LinkButton';
import { IconLoader2 } from '@tabler/icons-react';

type FormStatus = 'DEFAULT' | 'SUBMITTING' | 'SUCCESS' | 'ERROR';

export const HelpCentreList: React.FC<HelpCentreListInput> = ({
  title,
  builderState: {
    state: { helpCentrePages }, // Provided by Builder at page-level
  },
}) => {
  const [formStatus, setFormStatus] = useState<FormStatus>('DEFAULT');
  const [searchResults, setSearchResults] = useState<[] | null>(null);

  const handleSubmit = async (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.target as HTMLFormElement);
    const data = Object.fromEntries(formData.entries());
    const search = data.search as string;

    if (search === '') {
      setFormStatus('DEFAULT');
      return setSearchResults(null);
    }
    if (search.length < 3) return;

    setFormStatus('SUBMITTING');

    const response = await fetch(`/builder-home-rebrand/api/help-centre?s=${encodeURI(search)}`);
    if (!response.ok) return setFormStatus('ERROR');

    const res = await response.json();
    setSearchResults(res.result);
    setFormStatus('SUCCESS');
  };

  return (
    <>
      <h1>{title}</h1>
      <form onSubmit={handleSubmit} className="gap-regular mt-4 mb-16 flex flex-wrap">
        <label className="absolute h-0 w-0 opacity-0" htmlFor="help-search">
          Search frequently asked questions by entering your question here.
        </label>
        <input
          id="help-search"
          type="text"
          name="search"
          placeholder="Type your question here"
          className="tc-input w-full md:flex-8"
        />
        <button type="submit" disabled={formStatus === 'SUBMITTING'} className="tc-submit-button relative md:flex-2">
          Search
          {formStatus === 'SUBMITTING' ? (
            <IconLoader2
              aria-hidden={true}
              className="absolute top-0 right-[1em] bottom-0 m-auto inline animate-spin"
            />
          ) : null}
        </button>
        <div className={`bg-primary-100 w-full rounded-lg ${formStatus !== 'DEFAULT' ? 'p-4' : 'p-0'}`}>
          <div role="alert">
            {formStatus === 'SUBMITTING' && (
              <p className="mb-2">
                <strong>Searching, please wait.</strong>
              </p>
            )}
            {formStatus === 'SUCCESS' && searchResults && (
              <p className="mb-2">
                <strong>Found {searchResults.length} results.</strong>
              </p>
            )}
            {formStatus === 'ERROR' && (
              <p className="mb-2">
                <strong className="text-error">There was an error, please try again.</strong>
              </p>
            )}
          </div>
        </div>
      </form>

      {searchResults && <Accordion items={searchResults} />}

      {!searchResults && (
        <>
          <h2>Or choose a Help Centre Topic question below:</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {helpCentrePages.map((helpCentrePage, idx) => (
              <Card key={idx} className="flex flex-col">
                <div className="relative mb-4 h-50 w-full overflow-hidden rounded-md">
                  <img
                    className="h-50 min-w-full object-cover object-bottom"
                    src={helpCentrePage.data.featuredImage}
                    alt=""
                    role="presentation"
                  />
                </div>
                <h3 className="tc-heading-xs mt-0 mb-4">{helpCentrePage.data.title}</h3>
                <LinkButton label="Read more" link={helpCentrePage.data.url} size="smallPadded" variant="tertiary" />
              </Card>
            ))}
          </div>
        </>
      )}
    </>
  );
};
